import { call, select, put, takeEvery, delay, all } from 'redux-saga/effects';
import { ICONS } from 'constants/icons';
import Segment from 'helpers/segment';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';

import { CLASS_ENTITY_TYPE, MESSAGE_USER_ENTITY_TYPES } from 'constants/logic';
import { createErrorBanner } from 'components/Banner';
import {
  calendarAvailability,
  practitioners,
  venueInstructors,
  resources,
  messageUser,
  userSmsSubscriptionStatus as userSmsSubscriptionStatusEntity,
  conversationLimitStatus as conversationLimitStatusEntity,
} from 'store/saga/entities/bound';
import { selectActiveVenueId, selectActiveVenue, selectUserId, selectRouteQuery } from 'selectors/general';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { reportError } from 'helpers/error-logging';
import { makePath } from 'helpers/link-generation';
import { ACTIVE_VENUE_CHANGED } from 'features/App';
import { createSuccessSnackbar, createErrorSnackbar } from 'components/Snackbar';
import {
  UPDATE_RESERVATION_ATTENDANCE,
  doUpdateReservationAttendance,
} from 'features/YieldManagement/YieldManagement.saga';
import { selectIsDesktop, selectPageIsVisible } from 'selectors/ui';

import { sagaMessages as messages } from '../Appointments.messages';
import { messages as messageMemberMessages } from './components/MessageMember/MessageMember.messages';
import {
  selectAvailabilityCalendarDates,
  selectActiveCalendarView,
  selectConversationLimitStatus,
  selectConversationStatus,
} from './AvailabilityCalendar.selectors';
import { setSendingMessage, setUpdatingPhoneNumber } from '../reducer';

const CHANGE_START_DATE = 'CHANGE_START_DATE';
export const changeCalendarDates = startDate => ({
  type: CHANGE_START_DATE,
  startDate,
});

export const UPDATE_RESERVATION_CALENDAR_ATTENDANCE = 'UPDATE_RESERVATION_CALENDAR_ATTENDANCE';
export const updateCalendarReservationAttendance = (scheduleId, reservationId, attended) => ({
  type: UPDATE_RESERVATION_CALENDAR_ATTENDANCE,
  scheduleId,
  reservationId,
  attended,
});

export const CHANGE_PRACTITIONER = 'CHANGE_PRACTITIONER';
export const changePractitioner = practitioner => ({
  type: CHANGE_PRACTITIONER,
  payload: practitioner,
});

export const UPDATE_CALENDAR_VIEW = 'UPDATE_CALENDAR_VIEW';
export const updateCalendarView = view => ({
  type: UPDATE_CALENDAR_VIEW,
  view,
});

export const INITIATE_CONVERSATION = 'INITIATE_CONVERSATION';
export const initiateConversation = ({ reservationId }) => ({
  type: INITIATE_CONVERSATION,
  reservationId,
});

export const REQUEST_CONVERSATION_STATUS = 'REQUEST_CONVERSATION_STATUS';
export const requestConversationStatus = ({ reservationId }) => ({
  type: REQUEST_CONVERSATION_STATUS,
  reservationId,
});

export const UPDATE_PHONE_NUMBER = 'UPDATE_PHONE_NUMBER';
export const updatePhoneNumber = ({ phoneNumber, asyncFns }) => ({
  type: UPDATE_PHONE_NUMBER,
  phoneNumber,
  asyncFns,
});

const ONE_MINUTE = 1000 * 60;
const FIVE_MINUTES = ONE_MINUTE * 5;

export function* fetchAppointmentsCalendarData(dependencies, action = {}) {
  const venue = yield select(selectActiveVenue);
  const { isMsInventoryVenue } = venue;
  let venueId = action?.venueId || venue.venueId;
  let { startDate, endDate } = action;

  try {
    // venueId should be present but we're seeing errors because it's missing
    // try to save the situation
    if (!venueId) {
      venueId = yield select(selectActiveVenueId);
    }
    // don't make the call if we're still missing data
    if (!venueId) {
      // eslint-disable-next-line no-throw-literal
      throw { venueId };
    }

    if (!startDate || !endDate) {
      ({ startDate, endDate } = yield select(selectAvailabilityCalendarDates));
    }

    if (isMsInventoryVenue) {
      yield call(fetchEntityIfNeeded, practitioners, [dependencies, venueId]);
      yield call(fetchEntityIfNeeded, resources, [dependencies, venueId]);
    }

    yield call(fetchEntity, calendarAvailability, [dependencies, venueId, startDate, endDate]);
    yield call(fetchEntityIfNeeded, venueInstructors, [dependencies, venueId, CLASS_ENTITY_TYPE.APPOINTMENT]);
  } catch (error) {
    if (!(error instanceof Error)) {
      yield call(reportError, 'caught fetchAppointmentsCalendarData', error);
    }
  }
}

export function* pollForAppointmentsCalendarData(dependencies) {
  const isPageVisible = yield select(selectPageIsVisible);
  const timeout = isPageVisible ? ONE_MINUTE : FIVE_MINUTES;
  yield delay(timeout);
  yield call(fetchAppointmentsCalendarData, dependencies);
  yield call(pollForAppointmentsCalendarData, dependencies);
}

export function* handleRouteEnterCalendar(dependencies) {
  const { venueId, countryCodeShort } = yield select(selectActiveVenue);
  const view = yield select(selectActiveCalendarView);
  const { startDate, endDate } = yield select(selectAvailabilityCalendarDates);

  if (countryCodeShort === 'US') {
    yield call(fetchEntity, conversationLimitStatusEntity, [dependencies]);
  }

  yield call(fetchAppointmentsCalendarData, dependencies, { startDate, endDate });
  yield call(dependencies.navigate, makePath.appointmentsCalendar({ venueId, startDate, view }));
  yield call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.CALENDAR_PAGE_VIEWED);
  // Effects should be placed before pollForAppointmentsCalendarData since it runs a polling loop
  // and following methods won't be triggered.
  yield call(pollForAppointmentsCalendarData, dependencies);
}

export function* doUpdateCalendarReservationAttendance(dependencies, action) {
  try {
    yield call(doUpdateReservationAttendance, dependencies, action);
    yield call(fetchAppointmentsCalendarData, dependencies);
  } catch (error) {
    yield put(createErrorBanner(messages.error.cannotUpdateCalendarData));
  }
}

export function* doChangeCalendarStartDate(dependencies, action) {
  const { startDate: actionStartDate } = action;

  const venueId = yield select(selectActiveVenueId);
  const view = yield select(selectActiveCalendarView);
  const { practitionerId } = yield select(selectRouteQuery);

  const { startDate, endDate } = yield select(selectAvailabilityCalendarDates, { startDate: actionStartDate });

  yield call(dependencies.navigate, makePath.appointmentsCalendar({ venueId, startDate, view, practitionerId }));
  yield call(fetchAppointmentsCalendarData, dependencies, { startDate, endDate });
}

export function* doChangePractitioner(dependencies, action) {
  const venueId = yield select(selectActiveVenueId);
  const { startDate } = yield select(selectAvailabilityCalendarDates);

  const practitionerId = action.payload?.id;

  yield call(Segment.event, 'Click Staff Filter', { venueId, practitionerId });
  yield call(dependencies.navigate, makePath.appointmentsCalendar({ venueId, startDate, practitionerId }));
}

export function* doUpdateCalendarView(dependencies, action) {
  const venueId = yield select(selectActiveVenueId);
  const stateView = yield select(selectActiveCalendarView);
  const { view } = action;

  if (stateView === view) return;

  const { startDate, endDate } = yield select(selectAvailabilityCalendarDates, { view });

  yield call(Segment.event, 'Click Period Filter', { period: view, venueId });
  yield call(dependencies.navigate, makePath.appointmentsCalendar({ venueId, startDate, view }));
  yield call(fetchAppointmentsCalendarData, dependencies, { startDate, endDate });
}

export function* handleActiveVenueChange(client, action) {
  const { venueId } = action;
  const activeVenueId = yield select(selectActiveVenueId);

  if (venueId !== activeVenueId) {
    yield call(client.navigate, makePath.appointmentsCalendar({ venueId }));
    yield call(fetchAppointmentsCalendarData, client, { venueId });
  }
}

export function* doSendMemberMessage(dependencies, action) {
  try {
    const { reservationId } = action;
    const [venueId, dashboardUserId, isDesktop] = yield all([
      select(selectActiveVenueId),
      select(selectUserId),
      select(selectIsDesktop),
    ]);
    const device = isDesktop ? 'desktop' : 'mobile_web';
    const deviceTimestamp = new Date().toISOString();
    const { conversationCount } = yield select(selectConversationLimitStatus);
    const { recipientConversationEligible } = yield select(selectConversationStatus, reservationId);
    const activeConversations = conversationCount + 1;

    yield put(setSendingMessage(true));
    yield call(fetchEntity, messageUser, [
      dependencies,
      {
        venueId,
        reservationId,
        type: MESSAGE_USER_ENTITY_TYPES.POST,
      },
    ]);
    const snackbar = recipientConversationEligible
      ? createSuccessSnackbar(messages.success.userMessageInitiated)
      : createErrorSnackbar(messageMemberMessages.wrongAppVersion, {}, { icon: ICONS.alert });
    yield all([put(setSendingMessage(false)), put(snackbar)]);
    yield call(Segment.event, 'Initiate Message', {
      dashboardUserId,
      venueId,
      reservationId,
      device,
      deviceTimestamp,
      activeConversations,
    });
  } catch (e) {
    yield put(createErrorSnackbar(messages.error.genericError));
  }
}

export function* doRequestConversationStatus(dependencies, action) {
  try {
    const { reservationId } = action;
    const venueId = yield select(selectActiveVenueId);
    yield all([
      call(fetchEntity, userSmsSubscriptionStatusEntity, [dependencies]),
      call(fetchEntity, messageUser, [dependencies, { venueId, reservationId, type: MESSAGE_USER_ENTITY_TYPES.GET }]),
    ]);
  } catch (e) {
    yield put(createErrorSnackbar(messages.error.genericError));
  }
}

export function* doUpdatePhoneNumber(dependencies, action) {
  try {
    const {
      phoneNumber: newNumber,
      asyncFns: { resolveSubmit },
    } = action;
    const userId = yield select(selectUserId);
    const countryCode = newNumber.substring(0, 2);
    const phoneNumber = newNumber.substring(2);
    const smsSettings = {
      countryCode,
      phoneNumber,
    };
    yield call(fetchEntity, userSmsSubscriptionStatusEntity, [dependencies, { smsSettings, userId }]);
    yield all([
      call(resolveSubmit),
      put(createSuccessSnackbar(messages.success.phoneNumberUpdated)),
      put(setUpdatingPhoneNumber(false)),
    ]);
  } catch (error) {
    const {
      asyncFns: { rejectSubmit },
    } = action;
    yield all([
      put(createErrorSnackbar(messages.error.cannotUpdatePhoneNumber)),
      call(reportError, error),
      call(rejectSubmit),
    ]);
  }
}

export function* watchActions(dependencies) {
  yield takeEvery(UPDATE_RESERVATION_ATTENDANCE, fetchAppointmentsCalendarData, dependencies);
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, dependencies);
  yield takeEvery(CHANGE_START_DATE, doChangeCalendarStartDate, dependencies);
  yield takeEvery(UPDATE_RESERVATION_CALENDAR_ATTENDANCE, doUpdateCalendarReservationAttendance, dependencies);
  yield takeEvery(UPDATE_RESERVATION_CALENDAR_ATTENDANCE, fetchAppointmentsCalendarData, dependencies);
  yield takeEvery(CHANGE_PRACTITIONER, doChangePractitioner, dependencies);
  yield takeEvery(UPDATE_CALENDAR_VIEW, doUpdateCalendarView, dependencies);
  yield takeEvery(INITIATE_CONVERSATION, doSendMemberMessage, dependencies);
  yield takeEvery(REQUEST_CONVERSATION_STATUS, doRequestConversationStatus, dependencies);
  yield takeEvery(UPDATE_PHONE_NUMBER, doUpdatePhoneNumber, dependencies);
}
